import React, { FC, ReactElement, useMemo, useState } from "react";
import styles from "./Registreringsflyt.module.less";
import { Banner } from "@digipost/dds-banner-react";
import Translated from "../infrastructure/translation/Translated";
import { useUnrecoverableError } from "../infrastructure/errors/useUnrecoverableError";
import { useRegistrerVirksomhet } from "./useRegistrerVirksomhet";
import { useValgtForetak } from "./useValgtForetak";
import { useProkuraVirksomheter } from "./useProkuraVirksomheter";
import { useIntegrasjonsPartner } from "../PartnerOnboarding/useIntegrasjonsPartner";
import { LoadingPage } from "../LoadingPage/LoadingPage";
import {
  EnkelVirksomhetInfo,
  ForetakMedLenker,
} from "../infrastructure/api/api";
import { Outlet } from "react-router";
import { useSteps } from "../infrastructure/steps/useSteps";
import { analyticsAction } from "../infrastructure/analytics/functions";
import { AnalyticsCategory } from "../infrastructure/analytics/types";

export interface KontaktInfo {
  orgNr: string;
  virksomhetsNavn: string;
  companyEmail: string;
  personalEmail: string;
}

export type RegistreringContextType = {
  kontaktInfo?: KontaktInfo;
  setKontaktInfo: (kontaktInfo?: KontaktInfo) => void;
  isRegistering?: boolean;
  valgtForetak?: ForetakMedLenker;
  velgForetak: (foretak: ForetakMedLenker) => void;
  velgerForetak: boolean;
  prokuraForetak: ForetakMedLenker[];
  registrer: () => void;
  integrasjonsPartner?: EnkelVirksomhetInfo;

  nextStep: () => void;
  previousStep: () => void;
  reset: () => ReactElement;
};

const Registreringsflyt: FC<{ steps: string[] }> = ({
  steps,
}): React.ReactElement => {
  const { setCustomError } = useUnrecoverableError();
  const { nextStep, previousStep, reset } = useSteps(steps);

  const [kontaktInfo, setKontaktInfo] = useState<KontaktInfo>();
  const { isRegistering, registrerVirksomhet } = useRegistrerVirksomhet();

  const { prokuraForetak, harHentetProkuraForetak } = useProkuraVirksomheter();

  const { valgtForetak, velgForetak, alleredeRegistrert, velgerForetak } =
    useValgtForetak(() => nextStep());

  const { integrasjonsPartner, integrasjonsPartnerResolved } =
    useIntegrasjonsPartner();

  const registrer = () => {
    if (valgtForetak && kontaktInfo) {
      analyticsAction(
        "bruker fullførte registrering",
        integrasjonsPartner
          ? AnalyticsCategory.PARTNERONBOARDING
          : AnalyticsCategory.REGISTRERING,
      );
      registrerVirksomhet(valgtForetak, kontaktInfo);
    } else {
      setCustomError('"valgtForetak" eller "kontaktInfo" er ikke satt.');
    }
  };

  const resolvedDependencies = useMemo(
    () => harHentetProkuraForetak && integrasjonsPartnerResolved,
    [harHentetProkuraForetak, integrasjonsPartnerResolved],
  );

  if (!resolvedDependencies) {
    return <LoadingPage />;
  }

  const alleredeRegistrertBanner = alleredeRegistrert ? (
    <Banner variant={"warning"} className={styles.warningBanner} role={"alert"}>
      <Translated textKey={"virksomhet.velg.allerede_registrert"} />
    </Banner>
  ) : (
    <></>
  );

  return (
    <main className={styles.registreringContent}>
      {alleredeRegistrertBanner}
      <Outlet
        context={
          {
            kontaktInfo,
            setKontaktInfo,
            isRegistering,
            valgtForetak,
            velgForetak,
            velgerForetak,
            prokuraForetak,
            registrer,
            integrasjonsPartner,
            nextStep,
            previousStep,
            reset,
          } satisfies RegistreringContextType
        }
      />
    </main>
  );
};

export default Registreringsflyt;
